/**
 * v3.events — /v3/events (write + batch + poll).
 */

import type {
  RequestOptions,
  ApiResult,
  SessionEventPayload,
  SessionEventDto,
  SessionEventFilters,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface EventWriteResponse {
  success: boolean;
  count: number;
  ids: ReadonlyArray<string>;
}

export interface EventListResponse {
  success: boolean;
  count: number;
  events: ReadonlyArray<SessionEventDto>;
}

export class EventsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Fire-and-forget single event. Returns the 201 response shape.
   * Callers should NOT await this in UI-critical paths (Rule #8).
   */
  public write(req: SessionEventPayload, opts?: RequestOptions): Promise<EventWriteResponse> {
    return this.http.post<EventWriteResponse>('/v3/events', {
      ...opts,
      body: { event: req },
    });
  }

  /** Batch insert 1..500 events. */
  public writeBatch(
    events: ReadonlyArray<SessionEventPayload>,
    opts?: RequestOptions
  ): Promise<EventWriteResponse> {
    return this.http.post<EventWriteResponse>('/v3/events', {
      ...opts,
      body: { events },
    });
  }

  public listSince(
    since: string,
    filters?: Omit<SessionEventFilters, 'since'>,
    opts?: RequestOptions
  ): Promise<EventListResponse> {
    const query: Record<string, string | number | boolean | undefined> = { since };
    if (filters) Object.assign(query, filters);
    return this.http.get<EventListResponse>('/v3/events', { ...opts, query });
  }

  public list(
    filters: SessionEventFilters,
    opts?: RequestOptions
  ): Promise<EventListResponse> {
    return this.http.get<EventListResponse>('/v3/events', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }
}

export type { ApiResult };
